/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: pinListsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Applications module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for PIN Lists module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a PIN List in the form.
 * Pattern: /pbx/applications/pin-list/new?duplicate={id}
 */
export const getPinListDuplicateUrl = (id: string | number): string =>
  `/pbx/applications/pin-list/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given PIN List id.
 */
export const navigateToPinListDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getPinListDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a PIN List by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deletePinListById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pin-lists/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete PIN list';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};



